import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check, ChevronLeft, Heart, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { rm, cachedMenu, fetchMenu } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { PhoneFrame } from "@/components/customer/CustomerShell";

function OptionRow({ o, selected, onSelect }) {
  return (
    <button onClick={onSelect} data-testid={`option-${o.name.toLowerCase().replace(/\s+/g, "-")}`}
      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-sm transition-colors ${selected ? "border-[#B91C1C] bg-red-50" : "border-transparent"}`}>
      <span className="flex items-center gap-3 font-medium">
        <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${selected ? "border-[#B91C1C] bg-[#B91C1C]" : "border-slate-400"}`}>
          {selected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
        </span>
        {o.name}
      </span>
      <span className="font-semibold">{o.price_delta ? `${o.price_delta > 0 ? "+" : "-"}${rm(Math.abs(o.price_delta))}` : "Percuma"}</span>
    </button>
  );
}

export default function ItemDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const [m, setM] = useState(() => cachedMenu().find((x) => x.id === id) || null);
  const [opt, setOpt] = useState("");
  const [note, setNote] = useState("");
  const [qty, setQty] = useState(1);
  const [fav, setFav] = useState(false);
  const back = () => (window.history.state?.idx > 0 ? nav(-1) : nav("/"));

  useEffect(() => {
    fetchMenu().then((list) => setM(list.find((x) => x.id === id) || false)).catch(() => {});
  }, [id]);

  if (m === false) return <PhoneFrame><div className="p-6 text-sm text-slate-500" data-testid="detail-not-found">Menu tidak tersedia. <button onClick={() => nav("/")} className="text-[#B91C1C] font-semibold underline" data-testid="detail-not-found-home">Kembali ke menu</button></div></PhoneFrame>;
  if (!m) return <PhoneFrame><div className="p-6 text-sm text-slate-500">Memuatkan...</div></PhoneFrame>;
  const delta = m.options.find((o) => o.name === opt)?.price_delta || 0;
  const unit = m.price + delta;

  const submit = () => {
    add({ menu_id: m.id, name: m.name, image: m.image, option: opt, unit_price: unit, qty, note: note.trim() });
    toast.success(`${m.name} ditambah ke troli`);
    back();
  };

  return (
    <PhoneFrame>
      <div className="flex items-center justify-between px-4 h-14 bg-white">
        <button onClick={back} data-testid="detail-back-button" className="p-1"><ChevronLeft className="w-6 h-6" /></button>
        <button onClick={() => setFav(!fav)} data-testid="detail-fav-button" className="p-1">
          <Heart className={`w-6 h-6 transition-colors ${fav ? "fill-red-500 text-red-500" : ""}`} />
        </button>
      </div>
      <img src={m.image || "/img/logo.png"} alt={m.name} className={`w-full h-64 sm:h-auto sm:aspect-[4/3] sm:max-h-96 ${m.image ? "object-cover" : "object-contain p-10 bg-amber-50"}`} data-testid="detail-image" />
      <div className="px-5 pt-5 pb-32 rise">
        <h1 className="font-display text-xl font-bold" data-testid="detail-name">{m.name}</h1>
        <p className="font-display text-2xl font-extrabold text-[#B91C1C] mt-1" data-testid="detail-price">{rm(unit)}</p>
        <p className="text-sm text-slate-600 mt-2">{m.description}</p>
        {m.options.length > 0 && (
          <>
            <h2 className="font-display font-bold text-base mt-6 mb-2">JIKA NAK TAMBAH <span className="text-xs font-medium text-slate-500">(tidak wajib, tekan lagi untuk buang)</span></h2>
            <div className="space-y-1">
              {m.options.map((o) => <OptionRow key={o.name} o={o} selected={opt === o.name} onSelect={() => setOpt(opt === o.name ? "" : o.name)} />)}
            </div>
          </>
        )}
        <h2 className="font-display font-bold text-base mt-6 mb-2">Nota <span className="font-normal text-sm text-slate-500">(pilihan)</span></h2>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="detail-note-input" rows={3} placeholder="Cth: Kurang pedas / Asing sambal"
          className="w-full rounded-xl border border-slate-200 bg-white p-3 text-sm outline-none focus:ring-2 focus:ring-[#B91C1C]" />
      </div>
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full md:max-w-md bg-white border-t px-5 py-4 flex items-center gap-4 z-40">
        <div className="flex items-center gap-4">
          <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus" className="p-1 active:scale-90 transition-transform"><Minus className="w-5 h-5" strokeWidth={2.5} /></button>
          <span className="font-bold w-4 text-center" data-testid="qty-value">{qty}</span>
          <button onClick={() => setQty(qty + 1)} data-testid="qty-plus" className="p-1 active:scale-90 transition-transform"><Plus className="w-5 h-5" strokeWidth={2.5} /></button>
        </div>
        <button onClick={submit} data-testid="add-to-cart-button"
          className="flex-1 h-12 rounded-xl bg-[#B91C1C] text-white font-semibold active:scale-95 transition-transform hover:bg-[#7F1D1D]">
          Tambah ke Troli · {rm(unit * qty)}
        </button>
      </div>
    </PhoneFrame>
  );
}
